/* ============================================================
   BLC Motion Engine — v2 "Fable Build"
   Scroll reveals, word-split headlines, soft parallax.
   No dependencies. Reduced-motion safe.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Word splitting for .blc-words elements ---- */
  function splitWords(el) {
    if (el.dataset.blcSplit) return;
    el.dataset.blcSplit = '1';
    var nodes = Array.prototype.slice.call(el.childNodes);
    el.textContent = '';
    var wordIndex = 0;

    nodes.forEach(function (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            el.appendChild(document.createTextNode(' '));
          } else {
            var w = document.createElement('span');
            w.className = 'blc-word';
            var inner = document.createElement('span');
            inner.className = 'blc-word-inner';
            inner.textContent = part;
            inner.style.setProperty('--blc-word-i', wordIndex++);
            w.appendChild(inner);
            el.appendChild(w);
          }
        });
      } else if (node.nodeName === 'BR') {
        el.appendChild(document.createElement('br'));
      } else {
        // Wrap element nodes (em, span) as a single word unit
        var wrap = document.createElement('span');
        wrap.className = 'blc-word';
        var innerWrap = document.createElement('span');
        innerWrap.className = 'blc-word-inner';
        innerWrap.style.setProperty('--blc-word-i', wordIndex++);
        innerWrap.appendChild(node);
        wrap.appendChild(innerWrap);
        el.appendChild(wrap);
      }
    });
  }

  /* ---- IntersectionObserver reveals ---- */
  function initReveals() {
    var targets = document.querySelectorAll('.blc-reveal, .blc-words');
    if (!('IntersectionObserver' in window) || reduced) {
      targets.forEach(function (t) { t.classList.add('blc-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('blc-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach(function (t) {
      if (t.classList.contains('blc-words')) splitWords(t);
      io.observe(t);
    });

    var pending = Array.prototype.slice.call(targets);

    function catchUp() {
      if (!pending.length) return;
      var vh = window.innerHeight;
      pending = pending.filter(function (t) {
        if (t.classList.contains('blc-in')) return false;
        var rect = t.getBoundingClientRect();
        // Reveal anything in view OR already scrolled past (fast scrolls /
        // anchor jumps can skip IntersectionObserver entirely).
        if (rect.top < vh * 0.92) {
          t.classList.add('blc-in');
          io.unobserve(t);
          return false;
        }
        return true;
      });
    }

    // Safety net for in-app browsers that delay IO on first paint.
    setTimeout(catchUp, 1200);

    var cuTick = false;
    window.addEventListener('scroll', function () {
      if (cuTick || !pending.length) return;
      cuTick = true;
      requestAnimationFrame(function () {
        cuTick = false;
        catchUp();
      });
    }, { passive: true });
  }

  /* ---- Soft parallax: data-blc-parallax="0.12" ---- */
  function initParallax() {
    if (reduced) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-blc-parallax]'));
    if (!els.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return;
        var speed = parseFloat(el.dataset.blcParallax) || 0.1;
        var center = rect.top + rect.height / 2 - vh / 2;
        el.style.transform = 'translateY(' + (-center * speed).toFixed(1) + 'px)';
      });
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---- Ink-up: scroll-linked per-word opacity for .blc-inkup ---- */
  function initInkup(scope) {
    var els = Array.prototype.slice.call((scope || document).querySelectorAll('.blc-inkup'));
    if (!els.length) return;

    els.forEach(function (el) {
      if (!el.dataset.blcSplit) splitWords(el);
    });

    if (reduced) {
      els.forEach(function (el) {
        el.querySelectorAll('.blc-word-inner').forEach(function (w) { w.style.opacity = 1; });
      });
      return;
    }

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        if (!el.isConnected) return;
        var rect = el.getBoundingClientRect();
        if (rect.top > vh || rect.bottom < 0) return;
        // Progress: 0 when element top hits 85% of viewport, 1 when top hits 30%
        var start = vh * 0.85;
        var end = vh * 0.3;
        var p = (start - rect.top) / (start - end);
        p = Math.max(0, Math.min(1, p));
        var words = el.querySelectorAll('.blc-word-inner');
        var n = words.length;
        for (var i = 0; i < n; i++) {
          var w = Math.max(0.14, Math.min(1, p * (n + 3) - i));
          words[i].style.opacity = w.toFixed(2);
        }
      });
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function init() {
    initReveals();
    initParallax();
    initInkup();
  }

  // Public API for sections that inject text dynamically (e.g. quiz screens)
  window.BLCMotion = {
    inkup: initInkup,
    split: splitWords
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init for Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   v10 Iconic chrome: cursor, scroll progress, announcement
   ticker, magnetic CTAs. All gated for touch/reduced-motion.
   ============================================================ */
(function () {
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer: fine)').matches;

  /* ---- Scroll progress line ---- */
  var bar = document.createElement('div');
  bar.className = 'blc-progress';
  document.body.appendChild(bar);
  var ticking = false;
  function paintProgress() {
    var max = document.documentElement.scrollHeight - window.innerHeight;
    var p = max > 0 ? window.scrollY / max : 0;
    bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, p)) + ')';
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(paintProgress); }
  }, { passive: true });
  paintProgress();

  /* ---- Announcement bar → ticker ---- */
  var ann = document.querySelector("#header-group [class*='announcement']");
  if (ann && !ann.querySelector('.blc-ticker')) {
    var texts = [];
    ann.querySelectorAll('p, span, div').forEach(function (el) {
      var own = Array.prototype.filter.call(el.childNodes, function (n) { return n.nodeType === 3; })
        .map(function (n) { return n.textContent.trim(); }).join(' ').trim();
      if (own && texts.indexOf(own) === -1) texts.push(own);
    });
    if (!texts.length && ann.textContent.trim()) texts = [ann.textContent.trim()];
    texts = texts.concat(['Free shipping', '30-day money-back guarantee']);
    if (texts.length) {
      var unit = texts.map(function (t) { return '<span>' + t + '</span><span aria-hidden="true">✦</span>'; }).join('');
      var ticker = document.createElement('div');
      ticker.className = 'blc-ticker';
      ticker.setAttribute('aria-label', texts.join('. '));
      var trackHtml = '<div class="blc-ticker__track" aria-hidden="true">' + unit + unit + unit + '</div>';
      ticker.innerHTML = trackHtml + trackHtml;
      ann.innerHTML = '';
      ann.appendChild(ticker);
    }
  }

  /* ---- Header: persistent buy CTA ---- */
  (function () {
    var header = document.querySelector('#header-group header');
    if (!header || header.querySelector('.blc-header-cta')) return;
    var anchor = header.querySelector('.header__column--right') ||
      header.querySelector('[class*="header__column"]:last-child');
    if (!anchor) return;
    var a = document.createElement('a');
    a.href = '/products/bright-bikini-line-serum-ingrown-hairs-discoloration';
    a.className = 'blc-btn blc-btn--ink blc-header-cta';
    a.textContent = 'Get BBL Serum';
    anchor.insertBefore(a, anchor.firstChild);
  })();

  /* ---- Magnetic CTAs ---- */
  if (fine && !reduced) {
    document.querySelectorAll('.blc-btn').forEach(function (btn) {
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
        var dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
        btn.style.transform = 'translate(' + dx * 5 + 'px,' + (dy * 4 - 2) + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform = '';
      });
    });
  }
})();
